// Transformation & Symmetry Knowledge Graph Samples
// Organized by difficulty levels with systematic naming

export const transformationSymmetrySamples = {
  fundamental: [
    {
      id: 'transform_fund_01',
      problem: "Reflect the point (3, 4) over the x-axis",
      problemType: "transformation_symmetry",
      difficulty: "fundamental",
      description: "Basic reflection over x-axis",
      graphData: {
        nodes: [
          {
            id: "original_point",
            name: "Original Point",
            type: "concepts",
            description: "The point to be reflected",
            value: "(3, 4)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "x_axis",
            name: "X-Axis",
            type: "concepts",
            description: "The line of reflection",
            value: "y = 0",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "reflection_rule",
            name: "Reflection Rule",
            type: "formulas",
            description: "Over x-axis: (x, y) → (x, -y)",
            value: "(x, y) → (x, -y)",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "step1_identify_point",
            name: "Step 1: Identify Point",
            type: "steps",
            description: "Identify the original point",
            value: "Step 1: Original point = (3, 4)",
            importance: 9,
            x: -100, y: 50, z: 25
          },
          {
            id: "step2_apply_rule",
            name: "Step 2: Apply Rule",
            type: "steps",
            description: "Apply reflection rule over x-axis",
            value: "Step 2: (3, 4) → (3, -4)",
            importance: 10,
            x: 0, y: 50, z: 25
          },
          {
            id: "step3_write_answer",
            name: "Step 3: Write Answer",
            type: "steps",
            description: "Write the reflected point",
            value: "Step 3: Reflected point = (3, -4)",
            importance: 9,
            x: 100, y: 50, z: 25
          },
          {
            id: "reflected_point",
            name: "Reflected Point",
            type: "values",
            description: "The final reflected point",
            value: "(3, -4)",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "original_point", to: "reflection_rule", type: "transformed by", strength: 9 },
          { from: "x_axis", to: "reflection_rule", type: "defines", strength: 9 },
          { from: "original_point", to: "step1_identify_point", type: "used in", strength: 9 },
          { from: "reflection_rule", to: "step2_apply_rule", type: "used in", strength: 10 },
          { from: "step1_identify_point", to: "step2_apply_rule", type: "leads to", strength: 10 },
          { from: "step2_apply_rule", to: "step3_write_answer", type: "leads to", strength: 10 },
          { from: "step3_write_answer", to: "reflected_point", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'transform_fund_02',
      problem: "Reflect the point (-2, 5) over the y-axis",
      problemType: "transformation_symmetry",
      difficulty: "fundamental",
      description: "Basic reflection over y-axis",
      graphData: {
        nodes: [
          {
            id: "original_point",
            name: "Original Point",
            type: "concepts",
            description: "The point to be reflected",
            value: "(-2, 5)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "y_axis",
            name: "Y-Axis",
            type: "concepts",
            description: "The line of reflection",
            value: "x = 0",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "reflection_rule",
            name: "Reflection Rule",
            type: "formulas",
            description: "Over y-axis: (x, y) → (-x, y)",
            value: "(x, y) → (-x, y)",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Apply reflection rule",
            value: "(-2, 5) → (2, 5)",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "reflected_point",
            name: "Reflected Point",
            type: "values",
            description: "The final reflected point",
            value: "(2, 5)",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "original_point", to: "reflection_rule", type: "transformed by", strength: 9 },
          { from: "y_axis", to: "reflection_rule", type: "defines", strength: 9 },
          { from: "reflection_rule", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "reflected_point", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'transform_fund_03',
      problem: "Translate the point (1, 3) by vector (2, -1)",
      problemType: "transformation_symmetry",
      difficulty: "fundamental",
      description: "Basic translation",
      graphData: {
        nodes: [
          {
            id: "original_point",
            name: "Original Point",
            type: "concepts",
            description: "The point to be translated",
            value: "(1, 3)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "translation_vector",
            name: "Translation Vector",
            type: "concepts",
            description: "The vector of translation",
            value: "(2, -1)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "translation_rule",
            name: "Translation Rule",
            type: "formulas",
            description: "Add vector to point: (x, y) + (a, b) = (x+a, y+b)",
            value: "(x, y) + (a, b) = (x+a, y+b)",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Apply translation rule",
            value: "(1, 3) + (2, -1) = (3, 2)",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "translated_point",
            name: "Translated Point",
            type: "values",
            description: "The final translated point",
            value: "(3, 2)",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "original_point", to: "translation_rule", type: "transformed by", strength: 9 },
          { from: "translation_vector", to: "translation_rule", type: "defines", strength: 9 },
          { from: "translation_rule", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "translated_point", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'transform_fund_04',
      problem: "Rotate the point (2, 2) 90° counterclockwise about the origin",
      problemType: "transformation_symmetry",
      difficulty: "fundamental",
      description: "Basic rotation about origin",
      graphData: {
        nodes: [
          {
            id: "original_point",
            name: "Original Point",
            type: "concepts",
            description: "The point to be rotated",
            value: "(2, 2)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "rotation_angle",
            name: "Rotation Angle",
            type: "concepts",
            description: "The angle of rotation",
            value: "90° counterclockwise",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "rotation_rule",
            name: "Rotation Rule",
            type: "formulas",
            description: "90° CCW: (x, y) → (-y, x)",
            value: "(x, y) → (-y, x)",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Apply rotation rule",
            value: "(2, 2) → (-2, 2)",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "rotated_point",
            name: "Rotated Point",
            type: "values",
            description: "The final rotated point",
            value: "(-2, 2)",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "original_point", to: "rotation_rule", type: "transformed by", strength: 9 },
          { from: "rotation_angle", to: "rotation_rule", type: "defines", strength: 9 },
          { from: "rotation_rule", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "rotated_point", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'transform_fund_05',
      problem: "Dilate the point (4, 6) by a scale factor of 2",
      problemType: "transformation_symmetry",
      difficulty: "fundamental",
      description: "Basic dilation from origin",
      graphData: {
        nodes: [
          {
            id: "original_point",
            name: "Original Point",
            type: "concepts",
            description: "The point to be dilated",
            value: "(4, 6)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "scale_factor",
            name: "Scale Factor",
            type: "concepts",
            description: "The dilation factor",
            value: "2",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "dilation_rule",
            name: "Dilation Rule",
            type: "formulas",
            description: "From origin: (x, y) → (kx, ky)",
            value: "(x, y) → (2x, 2y)",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Apply dilation rule",
            value: "(4, 6) → (8, 12)",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "dilated_point",
            name: "Dilated Point",
            type: "values",
            description: "The final dilated point",
            value: "(8, 12)",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "original_point", to: "dilation_rule", type: "transformed by", strength: 9 },
          { from: "scale_factor", to: "dilation_rule", type: "defines", strength: 9 },
          { from: "dilation_rule", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "dilated_point", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  intermediate: [
    {
      id: 'transform_inter_01',
      problem: "Find the image of the line y = 2x + 1 under reflection over the x-axis",
      problemType: "transformation_symmetry",
      difficulty: "intermediate",
      description: "Reflection of a line",
      graphData: {
        nodes: [
          {
            id: "original_line",
            name: "Original Line",
            type: "concepts",
            description: "The line to be reflected",
            value: "y = 2x + 1",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "x_axis_reflection",
            name: "X-Axis Reflection",
            type: "concepts",
            description: "Reflection over x-axis",
            value: "(x, y) → (x, -y)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "substitution",
            name: "Substitution",
            type: "values",
            description: "Substitute y = 2x + 1",
            value: "y' = -(2x + 1) = -2x - 1",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "reflected_line",
            name: "Reflected Line",
            type: "values",
            description: "The equation of reflected line",
            value: "y = -2x - 1",
            importance: 10,
            x: 0, y: 100, z: -15
          }
        ],
        links: [
          { from: "original_line", to: "x_axis_reflection", type: "transformed by", strength: 9 },
          { from: "x_axis_reflection", to: "substitution", type: "gives", strength: 10 },
          { from: "substitution", to: "reflected_line", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'transform_inter_02',
      problem: "Translate the circle (x-2)² + (y-3)² = 4 by vector (1, -2)",
      problemType: "transformation_symmetry",
      difficulty: "intermediate",
      description: "Translation of a circle",
      graphData: {
        nodes: [
          {
            id: "original_circle",
            name: "Original Circle",
            type: "concepts",
            description: "The circle to be translated",
            value: "(x-2)² + (y-3)² = 4",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "translation_vector",
            name: "Translation Vector",
            type: "concepts",
            description: "The vector of translation",
            value: "(1, -2)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "center_translation",
            name: "Center Translation",
            type: "values",
            description: "Translate center (2, 3) by (1, -2)",
            value: "(2, 3) + (1, -2) = (3, 1)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "translated_circle",
            name: "Translated Circle",
            type: "values",
            description: "The equation of translated circle",
            value: "(x-3)² + (y-1)² = 4",
            importance: 10,
            x: 0, y: 100, z: -15
          }
        ],
        links: [
          { from: "original_circle", to: "translation_vector", type: "transformed by", strength: 9 },
          { from: "translation_vector", to: "center_translation", type: "gives", strength: 10 },
          { from: "center_translation", to: "translated_circle", type: "defines", strength: 10 }
        ]
      }
    },
    {
      id: 'transform_inter_03',
      problem: "Rotate the line y = x + 2 by 45° clockwise about the origin",
      problemType: "transformation_symmetry",
      difficulty: "intermediate",
      description: "Rotation of a line",
      graphData: {
        nodes: [
          {
            id: "original_line",
            name: "Original Line",
            type: "concepts",
            description: "The line to be rotated",
            value: "y = x + 2",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "rotation_angle",
            name: "Rotation Angle",
            type: "concepts",
            description: "The angle of rotation",
            value: "45° clockwise",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "rotation_matrix",
            name: "Rotation Matrix",
            type: "formulas",
            description: "45° CW: (x, y) → (x-y, x+y)/√2",
            value: "(x, y) → (x-y, x+y)/√2",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "substitution",
            name: "Substitution",
            type: "values",
            description: "Substitute y = x + 2",
            value: "y' = (x + (x+2))/√2 = (2x+2)/√2",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "rotated_line",
            name: "Rotated Line",
            type: "values",
            description: "The equation of rotated line",
            value: "y = (2x+2)/√2",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "original_line", to: "rotation_matrix", type: "transformed by", strength: 9 },
          { from: "rotation_angle", to: "rotation_matrix", type: "defines", strength: 9 },
          { from: "rotation_matrix", to: "substitution", type: "gives", strength: 10 },
          { from: "substitution", to: "rotated_line", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'transform_inter_04',
      problem: "Dilate the circle (x-1)² + (y-2)² = 9 by a scale factor of 3 from the origin",
      problemType: "transformation_symmetry",
      difficulty: "intermediate",
      description: "Dilation of a circle",
      graphData: {
        nodes: [
          {
            id: "original_circle",
            name: "Original Circle",
            type: "concepts",
            description: "The circle to be dilated",
            value: "(x-1)² + (y-2)² = 9",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "scale_factor",
            name: "Scale Factor",
            type: "concepts",
            description: "The dilation factor",
            value: "3",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "center_dilation",
            name: "Center Dilation",
            type: "values",
            description: "Dilate center (1, 2) by factor 3",
            value: "(1, 2) → (3, 6)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "radius_dilation",
            name: "Radius Dilation",
            type: "values",
            description: "Dilate radius 3 by factor 3",
            value: "3 × 3 = 9",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "dilated_circle",
            name: "Dilated Circle",
            type: "values",
            description: "The equation of dilated circle",
            value: "(x-3)² + (y-6)² = 81",
            importance: 10,
            x: 0, y: 100, z: -15
          }
        ],
        links: [
          { from: "original_circle", to: "scale_factor", type: "transformed by", strength: 9 },
          { from: "scale_factor", to: "center_dilation", type: "gives", strength: 10 },
          { from: "scale_factor", to: "radius_dilation", type: "gives", strength: 10 },
          { from: "center_dilation", to: "dilated_circle", type: "defines", strength: 10 },
          { from: "radius_dilation", to: "dilated_circle", type: "defines", strength: 10 }
        ]
      }
    },
    {
      id: 'transform_inter_05',
      problem: "Find the line of symmetry for the parabola y = x² - 4x + 3",
      problemType: "transformation_symmetry",
      difficulty: "intermediate",
      description: "Finding axis of symmetry",
      graphData: {
        nodes: [
          {
            id: "parabola",
            name: "Parabola",
            type: "concepts",
            description: "The given parabola",
            value: "y = x² - 4x + 3",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "vertex_formula",
            name: "Vertex Formula",
            type: "formulas",
            description: "x-coordinate of vertex = -b/(2a)",
            value: "x = -(-4)/(2×1) = 2",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "axis_of_symmetry",
            name: "Axis of Symmetry",
            type: "values",
            description: "The vertical line of symmetry",
            value: "x = 2",
            importance: 10,
            x: 0, y: 100, z: -15
          }
        ],
        links: [
          { from: "parabola", to: "vertex_formula", type: "analyzed by", strength: 9 },
          { from: "vertex_formula", to: "axis_of_symmetry", type: "gives", strength: 10 }
        ]
      }
    }
  ],
  
  harder: [
    {
      id: 'transform_hard_01',
      problem: "Find the composition of transformations: reflect over y-axis, then rotate 90° clockwise",
      problemType: "transformation_symmetry",
      difficulty: "harder",
      description: "Composition of transformations",
      graphData: {
        nodes: [
          {
            id: "point",
            name: "Point",
            type: "concepts",
            description: "Any point (x, y)",
            value: "(x, y)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "reflection_step",
            name: "Reflection Step",
            type: "values",
            description: "Reflect over y-axis",
            value: "(x, y) → (-x, y)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "rotation_step",
            name: "Rotation Step",
            type: "values",
            description: "Rotate 90° clockwise",
            value: "(-x, y) → (y, x)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "composition",
            name: "Composition",
            type: "values",
            description: "Combined transformation",
            value: "(x, y) → (y, x)",
            importance: 10,
            x: 0, y: 100, z: -15
          }
        ],
        links: [
          { from: "point", to: "reflection_step", type: "transformed by", strength: 9 },
          { from: "reflection_step", to: "rotation_step", type: "followed by", strength: 10 },
          { from: "rotation_step", to: "composition", type: "gives", strength: 10 }
        ]
      }
    },
    {
      id: 'transform_hard_02',
      problem: "Find the invariant points under the transformation (x, y) → (2x, 2y)",
      problemType: "transformation_symmetry",
      difficulty: "harder",
      description: "Finding fixed points",
      graphData: {
        nodes: [
          {
            id: "transformation",
            name: "Transformation",
            type: "concepts",
            description: "The given transformation",
            value: "(x, y) → (2x, 2y)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "invariant_condition",
            name: "Invariant Condition",
            type: "concepts",
            description: "Points that don't change",
            value: "(x, y) = (2x, 2y)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "equations",
            name: "Equations",
            type: "values",
            description: "Set up equations",
            value: "x = 2x and y = 2y",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "Solve the equations",
            value: "x = 0 and y = 0",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "invariant_point",
            name: "Invariant Point",
            type: "values",
            description: "The only fixed point",
            value: "(0, 0)",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "transformation", to: "invariant_condition", type: "analyzed by", strength: 9 },
          { from: "invariant_condition", to: "equations", type: "gives", strength: 10 },
          { from: "equations", to: "solution", type: "solves", strength: 10 },
          { from: "solution", to: "invariant_point", type: "gives", strength: 10 }
        ]
      }
    },
    {
      id: 'transform_hard_03',
      problem: "Find the image of the ellipse x²/4 + y²/9 = 1 under rotation by 30° about the origin",
      problemType: "transformation_symmetry",
      difficulty: "harder",
      description: "Rotation of an ellipse",
      graphData: {
        nodes: [
          {
            id: "original_ellipse",
            name: "Original Ellipse",
            type: "concepts",
            description: "The ellipse to be rotated",
            value: "x²/4 + y²/9 = 1",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "rotation_angle",
            name: "Rotation Angle",
            type: "concepts",
            description: "The angle of rotation",
            value: "30°",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "rotation_matrix",
            name: "Rotation Matrix",
            type: "formulas",
            description: "30° rotation matrix",
            value: "x' = xcos30° - ysin30°, y' = xsin30° + ycos30°",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "substitution",
            name: "Substitution",
            type: "values",
            description: "Substitute rotation formulas",
            value: "Complex substitution into ellipse equation",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "rotated_ellipse",
            name: "Rotated Ellipse",
            type: "values",
            description: "The equation of rotated ellipse",
            value: "Complex equation in x' and y'",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "original_ellipse", to: "rotation_matrix", type: "transformed by", strength: 9 },
          { from: "rotation_angle", to: "rotation_matrix", type: "defines", strength: 9 },
          { from: "rotation_matrix", to: "substitution", type: "gives", strength: 10 },
          { from: "substitution", to: "rotated_ellipse", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'transform_hard_04',
      problem: "Find the center of rotation that maps point A(1, 2) to A'(3, 0) and point B(4, 1) to B'(2, 3)",
      problemType: "transformation_symmetry",
      difficulty: "harder",
      description: "Finding center of rotation",
      graphData: {
        nodes: [
          {
            id: "point_a",
            name: "Point A",
            type: "concepts",
            description: "Original point A",
            value: "A(1, 2)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "point_a_prime",
            name: "Point A'",
            type: "concepts",
            description: "Image of point A",
            value: "A'(3, 0)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "point_b",
            name: "Point B",
            type: "concepts",
            description: "Original point B",
            value: "B(4, 1)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "point_b_prime",
            name: "Point B'",
            type: "concepts",
            description: "Image of point B",
            value: "B'(2, 3)",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "perpendicular_bisector",
            name: "Perpendicular Bisector",
            type: "concepts",
            description: "Method to find center",
            value: "Intersection of perpendicular bisectors",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "center",
            name: "Center of Rotation",
            type: "values",
            description: "The center point",
            value: "(2, 2)",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "point_a", to: "point_a_prime", type: "mapped to", strength: 9 },
          { from: "point_b", to: "point_b_prime", type: "mapped to", strength: 9 },
          { from: "point_a", to: "perpendicular_bisector", type: "used in", strength: 9 },
          { from: "point_b", to: "perpendicular_bisector", type: "used in", strength: 9 },
          { from: "perpendicular_bisector", to: "center", type: "gives", strength: 10 }
        ]
      }
    },
    {
      id: 'transform_hard_05',
      problem: "Find the equation of the image of the hyperbola x² - y² = 1 under the transformation (x, y) → (2x + y, x + 2y)",
      problemType: "transformation_symmetry",
      difficulty: "harder",
      description: "Linear transformation of hyperbola",
      graphData: {
        nodes: [
          {
            id: "original_hyperbola",
            name: "Original Hyperbola",
            type: "concepts",
            description: "The hyperbola to be transformed",
            value: "x² - y² = 1",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "linear_transformation",
            name: "Linear Transformation",
            type: "concepts",
            description: "The given transformation",
            value: "(x, y) → (2x + y, x + 2y)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "inverse_transformation",
            name: "Inverse Transformation",
            type: "formulas",
            description: "Find inverse to substitute back",
            value: "x = (2x' - y')/3, y = (-x' + 2y')/3",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "substitution",
            name: "Substitution",
            type: "values",
            description: "Substitute inverse into original",
            value: "((2x' - y')/3)² - ((-x' + 2y')/3)² = 1",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "simplified_equation",
            name: "Simplified Equation",
            type: "values",
            description: "The transformed hyperbola",
            value: "3x'² - 3y'² + 4x'y' = 9",
            importance: 10,
            x: 0, y: 100, z: -15
          }
        ],
        links: [
          { from: "original_hyperbola", to: "linear_transformation", type: "transformed by", strength: 9 },
          { from: "linear_transformation", to: "inverse_transformation", type: "requires", strength: 10 },
          { from: "inverse_transformation", to: "substitution", type: "gives", strength: 10 },
          { from: "substitution", to: "simplified_equation", type: "simplifies to", strength: 10 }
        ]
      }
    }
  ]
}; 